/**
 * HTTP 模块统一导出
 *
 * 注意：createHttpClient 只添加基础拦截器（错误处理、重试）
 * 公共参数拦截器由 CSDClient 在创建 DeviceInfoManager 后添加
 */

import type { IStorage } from '../storage/Storage'
import type { HttpConfig } from '../../types/api'
import { HttpClient } from './HttpClient'
import { browserAdapter } from './adapters/browser'
import { serverAdapter } from './adapters/server'
import { UniAppHttpAdapter } from './adapters/uniapp'
import { createErrorInterceptor, setStoragePrefix } from './interceptors/error'
import { createRetryInterceptor } from './interceptors/retry'

export * from './HttpClient'
export * from './adapters/browser'
export * from './adapters/server'
export * from './adapters/uniapp'
export * from './interceptors/auth'
export * from './interceptors/error'
export * from './interceptors/retry'
export * from './interceptors/params'

/**
 * 检测当前 HTTP 环境
 * 与 storage/index.ts 保持一致：有 window 就是浏览器族，uniapp 仅在无 window 的小程序原生环境下才选中
 */
function detectHttpEnvironment(): 'browser' | 'uniapp' | 'server' {
  // 有 window 就是浏览器/H5/WebView，用标准 fetch adapter
  if (typeof window !== 'undefined') {
    return 'browser'
  }

  // 无 window 时才考虑小程序原生环境，验证 uni.request 真实可调用
  const uniObj =
    (typeof uni !== 'undefined' ? uni : null) ??
    (typeof globalThis !== 'undefined' ? (globalThis as any).uni : null)
  if (uniObj && typeof uniObj.request === 'function') {
    return 'uniapp'
  }

  return 'server'
}

/**
 * 创建 HTTP 客户端（自动检测环境）
 *
 * 注意：此函数只添加基础拦截器，不添加公共参数拦截器
 * 公共参数拦截器需要 DeviceInfoManager，由 CSDClient 负责添加
 */
export function createHttpClient(
  config: HttpConfig,
  _storage: IStorage,
  sdkConfig?: { softId?: string; storagePrefix?: string; version?: string; os?: number }
): HttpClient {
  const env = detectHttpEnvironment()
  const adapter =
    env === 'uniapp' ? UniAppHttpAdapter : env === 'browser' ? browserAdapter : serverAdapter

  const client = new HttpClient(config, adapter)

  // 设置错误拦截器的 storage 前缀
  if (sdkConfig?.storagePrefix !== undefined) {
    setStoragePrefix(sdkConfig.storagePrefix)
  }

  // 添加错误处理拦截器
  client.addErrorInterceptor(createErrorInterceptor())

  // 添加重试拦截器
  client.addErrorInterceptor(createRetryInterceptor(client))

  return client
}

/**
 * 设置 CSD SDK 的登录回调
 * 用于在 403 错误时触发登录对话框
 */
export {
  setLoginRequiredCallback,
  resetLoginRequiredFlag,
  setStoragePrefix
} from './interceptors/error'
